import React, { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@tasama/ui';
import { useTranslation } from '@tasama/i18n';
import { CompanyTypeScreen } from '../components/CompanyType';
import { useCompanyTypeStore } from '../stores/company-type-store';
import type { CompanyTypeStore } from '../stores/company-type-store';
import { companyTypeSchema } from '../schemas/company-schemas';

export interface CompanyTypePageProps {
  onStatusChange?: (_value: string) => void;
  navigationButtons?: {
    showBack?: boolean;
    backText?: string;
    onBack?: () => void;
    showContinue?: boolean;
    continueText?: string;
    onContinue?: () => void;
    continueDisabled?: boolean;
  };
  className?: string;
  testID?: string;
}

export const CompanyTypePage: React.FC<CompanyTypePageProps> = ({
  onStatusChange,
  className,
  testID = 'company-type-page',
}) => {
  const { t } = useTranslation('onboarding');

  const beforeYouBegin = {
    title: t('companyType.beforeYouBegin.title'),
    description: t('companyType.beforeYouBegin.description'),
  };

  const sections = {
    companyType: {
      title: t('companyType.title'),
      description: t('companyType.description'),
      headerIcon: <Icon name="Building" size={24} />,
      companyOptions: [
        {
          label: t('companyType.options.international.label'),
          value: 'INTERNATIONAL',
          description: t('companyType.options.international.description'),
          icon: <Icon name="Globe" size={20} />,
          showBeforeYouBegin: true,
          beforeYouBeginCard: {
            ...beforeYouBegin,
            items: [
              {
                text: t(
                  'companyType.beforeYouBegin.items.companyOperationDetailsHomeCountry'
                ),
                checked: false,
              },
              {
                text: t(
                  'companyType.beforeYouBegin.items.otherCountriesRegistration'
                ),
                checked: false,
              },
            ],
          },
          testID: 'company-type-international-moving-to-ksa',
        },
        {
          label: t('companyType.options.ksaPrivate.label'),
          value: 'KSA_PRIVATE',
          description: t('companyType.options.ksaPrivate.description'),
          icon: <Icon name="Building2" size={20} />,
          showBeforeYouBegin: true,
          beforeYouBeginCard: {
            ...beforeYouBegin,
            items: [
              {
                text: t(
                  'companyType.beforeYouBegin.items.companyRegistrationCertificate'
                ),
                checked: false,
              },
              {
                text: t(
                  'companyType.beforeYouBegin.items.companyOperationDetails'
                ),
                checked: false,
              },
            ],
          },
          testID: 'company-type-private-sector-ksa',
        },
        {
          label: t('companyType.options.pifOwned.label'),
          value: 'pif-owned',
          description: t('companyType.options.pifOwned.description'),
          icon: <Icon name="Briefcase" size={20} />,
          showBeforeYouBegin: true,
          testID: 'company-type-pif-owned',
        },
      ],
    },
    companyStatus: {
      title: t('companyType.companyStatus.title'),
      description: t('companyType.companyStatus.description'),
      headerIcon: <Icon name="Building2" size={24} />,
      companyOptions: [
        {
          label: t('companyType.options.pifEstablished.label'),
          value: 'PIF_ESTABLISHED',
          description: t('companyType.options.pifEstablished.description'),
          icon: <Icon name="Briefcase" size={20} />,
          showBeforeYouBegin: true,
          beforeYouBeginCard: {
            ...beforeYouBegin,
            items: [
              {
                text: t(
                  'companyType.beforeYouBegin.items.companyRegistrationCertificate'
                ),
                checked: false,
              },
            ],
          },
          testID: 'company-status-established',
        },
        {
          label: t('companyType.options.pifUnderDevelopment.label'),
          value: 'PIF_UNDER_DEVELOPMENT',
          description: t('companyType.options.pifUnderDevelopment.description'),
          icon: <Icon name="Building2" size={20} />,
          showBeforeYouBegin: false,
          testID: 'company-status-pif-project',
        },
      ],
    },
  };

  const {
    companyType: companyTypeSection,
    companyStatus: companyStatusSection,
  } = sections;

  // Read persisted data reactively — restores selection when navigating back to this step
  const storedFormData = useCompanyTypeStore(
    (s: CompanyTypeStore) => s.formData
  );

  const setStoreValidity = useCompanyTypeStore(
    (s: CompanyTypeStore) => s.setValidity
  );
  const setStoreFormData = useCompanyTypeStore(
    (s: CompanyTypeStore) => s.setFormData
  );
  const clearAllOtherStores = useCompanyTypeStore(
    (s: CompanyTypeStore) => s.clearAllOtherStores
  );

  const {
    watch,
    control,
    setValue,
    reset,
    formState: { isValid },
  } = useForm({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(companyTypeSchema as any),
    mode: 'onTouched',
    defaultValues: {
      companyType: storedFormData.companyType || '',
      companyStatus: storedFormData.companyStatus || undefined,
    },
  });

  useEffect(() => {
    if (!storedFormData.companyType) {
      reset({ companyType: '', companyStatus: undefined });
    }
  }, [storedFormData.companyType, reset]);

  useEffect(() => {
    const sub = watch((values) => {
      setStoreFormData(values);
    });
    return () => sub.unsubscribe();
  }, [watch, setStoreFormData]);

  const formValues = useWatch({ control });
  const selectedCompanyType = formValues.companyType || '';

  useEffect(() => {
    setStoreValidity(isValid && !!selectedCompanyType);
  }, [isValid, selectedCompanyType, setStoreValidity]);

  const handleCompanyTypeChange = (value: string) => {
    const prev = formValues.companyType;
    setValue('companyType', value, {
      shouldValidate: true,
      shouldTouch: true,
    });
    if (value !== 'pif-owned') {
      setValue('companyStatus', undefined, { shouldValidate: true });
    }
    if (prev && prev !== value) {
      clearAllOtherStores();
    }
    onStatusChange?.(value);
  };

  const handleCompanyStatusChange = (value: string) => {
    setValue('companyStatus', value, {
      shouldValidate: true,
      shouldTouch: true,
    });
    onStatusChange?.(value);
  };

  return (
    <div className={className}>
      {/* Company Type Section - Always visible */}
      {companyTypeSection && (
        <CompanyTypeScreen
          title={companyTypeSection.title}
          description={companyTypeSection.description}
          headerIcon={companyTypeSection.headerIcon}
          companyOptions={companyTypeSection.companyOptions}
          handleStatusChange={handleCompanyTypeChange}
          selectedStatus={selectedCompanyType}
          radioGroupName="company-type"
          testID={`${testID}-company-type`}
          beforeYouBeginCard={
            companyTypeSection.companyOptions.find(
              (option) => option.value === selectedCompanyType
            )?.beforeYouBeginCard || undefined
          }
        />
      )}

      {/* Company Status Section - Only visible when 'pif-owned' is selected */}
      {selectedCompanyType === 'pif-owned' && companyStatusSection && (
        <CompanyTypeScreen
          title={companyStatusSection.title}
          description={companyStatusSection.description}
          headerIcon={companyStatusSection.headerIcon}
          titleSize="sm"
          companyOptions={companyStatusSection.companyOptions}
          beforeYouBeginCard={
            companyStatusSection.companyOptions.find(
              (option) => option.value === formValues.companyStatus
            )?.beforeYouBeginCard || undefined
          }
          handleStatusChange={handleCompanyStatusChange}
          selectedStatus={formValues.companyStatus || ''}
          radioGroupName="company-status"
          testID={`${testID}-company-status`}
          className="mt-10 md:mt-5"
        />
      )}
    </div>
  );
};

CompanyTypePage.displayName = 'CompanyTypePage';

export default CompanyTypePage;
